import { FormEvent, useState, useEffect, ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { AddPaymentType, defaultAddPaymentType } from '../types/paymentValidations';
import { Button, Input } from '../../../shared';
import { addPaymentMethod } from '../../../store/paymentMethods/paymentMethodsSlice';
import { createPaymentMethod, getFortisClientToken } from '../../../api/paymentMethods';
import { 
  ButtonsWrapper, 
  CancelButton, 
  Title, 
  Wrapper, 
  SpinnerOverlay, 
  Spinner,
  ExpirationDateWrapper,
  MonthSelect,
  YearSelect
} from './CreatePaymentMethod.styled';
import { ErrorText, FullRow, HalfRow } from '../Forms.styled';
import { Wrapper as InputWrapper, Label, ErrorText as InputErrorText } from '../../../shared/Input/Input.styled';
import type { state as StateType } from '../../../types/store/state';

interface Props {
  setShowAddPayment: (value: boolean) => void;
}

export const CreatePaymentMethod = (props: Props) => {
  const { t } = useTranslation();
  const { primaryColor } = useSelector((state: StateType) => state.ui.pallette);
  const { paymentProcessor } = useSelector((state: StateType) => state.branchStore);
  const dispatch = useDispatch();

  const [errors, setErrors] = useState<AddPaymentType>(defaultAddPaymentType);
  const [cardNumber, setCardNumber] = useState<string>('');
  const [nameOnCard, setNameOnCard] = useState<string>('');
  const [cvc, setCvc] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expMonth, setExpMonth] = useState<string>('');
  const [expYear, setExpYear] = useState<string>('');

  const months = Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    return {
      value: month < 10 ? `0${month}` : `${month}`,
      label: month < 10 ? `0${month}` : `${month}`,
    };
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => {
    const year = currentYear + i;
    return {
      value: `${year}`,
      label: `${year}`,
    };
  });

  useEffect(() => {
    if (paymentProcessor?.name === 'fortis') {
      handleFortisToken();
    }
  }, []);

  const formatCardNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    const limitedDigits = digits.slice(0, 19);
    const formattedValue = limitedDigits.replace(/(\d{4})(?=\d)/g, '$1 ');
    return formattedValue;
  };

  const handleCardNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setCardNumber(formatted);
  };

  const handleNameOnCardChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
    setNameOnCard(value);
  };

  const handleCvcChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setCvc(value);
  };

  const handleExpMonthChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setExpMonth(e.target.value);
  };

  const handleExpYearChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setExpYear(e.target.value);
  };

  const handleSaveCard = async (paramsToSend: any) => {
    setIsLoading(true);
    try {
      const paymentMethod = await toast.promise(
        createPaymentMethod({
          params: paramsToSend,
        }),
        {
          pending: {
            render() {
              return `${t('payment.create.toastify.pending')}`;
            },
            icon: '⚠️',
          },
          success: {
            render() {
              return `${t('payment.create.toastify.success')}`;
            },
            icon: '✅',
          },
          error: {
            render({data}: any) {
              if (data.response.status > 400) {
                return `${t('payment.create.toastify.error')}`;
              }
              return `${data.response.data.detail}`;
            },
            icon: '❗',
          }
        }
      );
      await dispatch(addPaymentMethod(paymentMethod));
      await props.setShowAddPayment(false);
    }
    catch (error: any) {
      await props.setShowAddPayment(false);
      throw new Error(error);
    }
    finally {
      setIsLoading(false);
    }
  };

  const handleFortisToken = async () => {
    setIsLoading(true);
    try {
      const fortisCall = await getFortisClientToken({
        url: paymentProcessor.config.apiUrl,
        locationId: paymentProcessor.config.locationId,
        userId: paymentProcessor.config.userId,
        apiKey: paymentProcessor.config.apiKey,
        developerId: paymentProcessor.config.developerId,
      });

      const CommerceElements = await new (window as any).Commerce.elements(fortisCall.data.client_token);

      await CommerceElements
        .create({
          container: '#payment_form',
          theme: 'default',
          showReceipt: false,
          environment: paymentProcessor.config.environment,
          appearance: {
            colorButtonBackground: primaryColor,
            colorButtonActionBackground: primaryColor,
          },
        });

      await CommerceElements
        .on('done', async (result: any) => {
          const paramsToSend = {
            ticket_id: result.data.id,
          };

          await handleSaveCard(paramsToSend);
        });

      await CommerceElements
        .on('error', (error: any) => {
          toast.error(`${t('payment.create.toastify.error')}`);
          setIsLoading(false);
        });
    } catch (error: any) {
      setIsLoading(false);
      throw new Error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const rawCardNumber = cardNumber.replace(/\s/g, '');
      const newErrors: AddPaymentType = {...defaultAddPaymentType};
      let hasErrors = false;

      if (rawCardNumber.length < 15 || rawCardNumber.length > 19) {
        newErrors.number.push(t('payment.errors.invalidCardNumber'));
        hasErrors = true;
      }

      if (cvc.length < 3 || cvc.length > 4) {
        newErrors.cvc.push(t('payment.errors.invalidCvc'));
        hasErrors = true;
      }

      if (!expMonth || !expYear) {
        newErrors.expMonth.push(t('payment.errors.requiredExpiration'));
        hasErrors = true;
      }

      if (hasErrors) {
        setErrors(newErrors);
        setIsLoading(false);
        return;
      }

      const paramsToSend = {
        number: rawCardNumber,
        expMonth: expMonth,
        expYear: expYear,
        cvc: cvc,
      };

      await handleSaveCard(paramsToSend);
    } catch (error: any) {
      if (error.response && error.response.status === 400) {
        setErrors({
          ...errors,
          ...error.response.data,
        });
      } else {
        throw new Error(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (paymentProcessor?.name === 'fortis') {
    return (
      <>
        {isLoading && (
          <SpinnerOverlay>
            <Spinner />
          </SpinnerOverlay>
        )}
        <div id="payment_form" />
      </>
    );
  }

  return (
    <>
      {isLoading && (
        <SpinnerOverlay>
          <Spinner />
        </SpinnerOverlay>
      )}
      <form onSubmit={onSubmit}>
        <Wrapper>
          <Title>
            {t('payment.addPayMethods.title')}
          </Title>
          <FullRow>
            <Input
              label={t('payment.nameOnCard.title')}
              name="nameOnCard"
              type="text"
              placeholder={t('payment.nameOnCard.placeholder')}
              error={errors.nameOnCard}
              value={nameOnCard}
              onChange={handleNameOnCardChange}
              required
            />
          </FullRow>
          <FullRow>
            <Input
              label={t('payment.cardNumber.title')}
              name="number"
              type="text"
              placeholder={t('payment.cardNumber.placeholder')}
              error={errors.number}
              value={cardNumber}
              onChange={handleCardNumberChange}
              maxLength={23}
              required
            />
          </FullRow>
          <HalfRow>
            <InputWrapper>
              <Label>{t('payment.expDate.title')}</Label>
              <ExpirationDateWrapper>
                <MonthSelect
                  name="expMonth"
                  value={expMonth}
                  onChange={handleExpMonthChange}
                  required
                  defaultValue=""
                >
                  <option value="" disabled>MM</option>
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </MonthSelect>
                <YearSelect
                  name="expYear"
                  value={expYear}
                  onChange={handleExpYearChange}
                  required
                  defaultValue=""
                >
                  <option value="" disabled>YYYY</option>
                  {years.map((year) => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </YearSelect>
              </ExpirationDateWrapper>
              {errors.expMonth.length > 0 && (
                <InputErrorText>{errors.expMonth[0]}</InputErrorText>
              )}
            </InputWrapper>
            <Input
              label={t('payment.cvv.title')}
              name="cvc"
              placeholder={t('payment.cvv.placeholder')}
              type="text"
              maxLength={4}
              value={cvc}
              onChange={handleCvcChange}
              error={errors.cvc}
              required
            />
          </HalfRow>
          {errors.nonFieldErrors.length ? <ErrorText>{errors.nonFieldErrors[0]}</ErrorText> : null}
        </Wrapper>
        <ButtonsWrapper>
          <CancelButton
            title={t('shared.action.cancel')}
            type="reset"
            onClick={() => props.setShowAddPayment(false)}
            disabled={isLoading}
          />
          <Button
            title={t('shared.action.save')}
            type="submit"
            disabled={isLoading}
          />
        </ButtonsWrapper>
      </form>
    </>
  );
};